import React, { useEffect, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { Filter, FilterInput, FilterSelectNative, FilterCombobox } from '@/components/custom/filter';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import getIcon from '@/utils/getIcon';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useNote } from '@/hooks/useNote';
import usePermission from '@/hooks/usePermission';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import { NotificationsToggle, NotificationsWrapper } from '@/components/custom/notifications';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { SearchIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export const SearchWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-search',
      // styles
      '',
      className
    )}
    {...props}
  />
));
SearchWrapper.displayName = 'SearchWrapper';

export const SearchHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-search-header',
      // styles
      'mb-6 border-b border-solid border-neutral-lightest pb-6',
      className
    )}
    {...props}
  />
));
SearchHeader.displayName = 'SearchHeader';

export const SearchBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-search-body',
      // styles
      'flex h-[440px] flex-col overflow-y-auto pr-3',
      className
    )}
    {...props}
  />
));
SearchBody.displayName = 'SearchBody';

export const SearchLoaderWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-search-loader-wrapper',
      // styles
      'm-auto text-center',
      className
    )}
    {...props}
  />
));
SearchLoaderWrapper.displayName = 'SearchLoaderWrapper';

export const SearchLoaderTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-search-loader-title',
      // styles
      'mb-0 text-base leading-normal text-neutral',
      className
    )}
    {...props}
  />
));
SearchLoaderTitle.displayName = 'SearchLoaderTitle';

export const SearchResult = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-search-result',
      // styles
      'flex cursor-pointer items-start gap-5 border-b border-solid border-primary-lightest bg-white px-1 py-4',
      className
    )}
    {...props}
  />
));
SearchResult.displayName = 'SearchResult';

export const SearchResultIcon = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-search-result-icon',
      // styles
      'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-lightest text-primary',
      className
    )}
    {...props}
  />
));
SearchResultIcon.displayName = 'SearchResultIcon';

export const SearchResultContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-search-result-content',
      // styles
      'w-0 flex-grow',
      className
    )}
    {...props}
  />
));
SearchResultContent.displayName = 'SearchResultContent';

export const SearchResultHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-search-result-header',
      // styles
      'mb-3',
      className
    )}
    {...props}
  />
));
SearchResultHeader.displayName = 'SearchResultHeader';

export const SearchResultTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-search-result-title',
      // styles
      'mb-0 text-[12px] font-bold text-primary-dark',
      className
    )}
    {...props}
  />
));
SearchResultTitle.displayName = 'SearchResultTitle';

export const SearchResultBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-search-result-body',
      // styles
      '',
      className
    )}
    {...props}
  />
));
SearchResultBody.displayName = 'SearchResultBody';

export const SearchResultText = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-search-result-text',
      // styles
      'mb-0 truncate text-[12px] font-light text-neutral-dark',
      className
    )}
    {...props}
  />
));
SearchResultText.displayName = 'SearchResultText';

export const SearchResultDate = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-search-result-date',
      // styles
      'mb-0 shrink-0 text-[12px] font-bold text-neutral-dark',
      className
    )}
    {...props}
  />
));
SearchResultDate.displayName = 'SearchResultDate';

export function Search() {
  const navigate = useNavigate();
  const client = useClient();
  const timerRef = useRef(null);
  const authorityLevel = useAuthorityLevel();
  const { abilities } = usePlatformAbilities();
  const { showNotes, setShowNotes, setNotesModalOpen, setActiveNote } = useNote();
  const isStudent = authorityLevel === 'student';

  const [isOpen, setIsOpen] = useState(false);
  const [searchData, setSearchData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const filters = useFormik({
    initialValues: {
      updated: 'ever',
      value: '',
      except: []
    }
  });

  const isEmptyField = filters.values.value === '';

  const lastUpdateOptions = [
    { title: 'Hoje', id: 'today' },
    { title: 'Ontem', id: 'yesterday' },
    { title: 'Semana passada', id: 'last_week' },
    { title: 'Mês passado', id: 'last_month' },
    { title: 'Ano passado', id: 'last_year' },
    { title: 'Sempre', id: 'ever' }
  ];

  const typeOptions = [
    {
      value: 'posts',
      label: 'Fórum',
      permission: usePermission('Visualizar postagens e comentários', 'Fórum')
    },
    {
      value: 'courses',
      label: 'Cursos',
      permission: usePermission(isStudent ? 'Visualizar lista de módulos e seus conteúdos' : 'Visualizar lista de cursos', 'Cursos')
    },
    {
      value: 'learning_systems',
      label: 'Módulos',
      permission: usePermission(isStudent ? 'Visualizar lista de módulos e seus conteúdos' : 'Visualizar lista de cursos', 'Cursos')
    },
    {
      value: 'learning_system_items',
      label: 'Aulas',
      permission: usePermission(isStudent ? 'Visualizar lista de módulos e seus conteúdos' : 'Visualizar lista de cursos', 'Cursos')
    },
    {
      value: 'question_books',
      label: 'Avaliações',
      permission: usePermission('Visualizar lista de provas', 'Provas e simulados')
    },
    {
      value: 'essays',
      label: 'Redação',
      permission: usePermission('Visualizar redação', 'Redação')
    },
    {
      value: 'auditoriums',
      label: 'Monitorias',
      permission: usePermission('Visualizar monitorias', 'Monitorias')
    },
    {
      value: 'live_classrooms',
      label: 'Aulas ao vivo',
      permission: usePermission('Visualizar aulas ao vivo', 'Aulas ao vivo')
    },
    {
      value: 'notes',
      label: 'Anotações',
      permission: usePermission('Visualizar anotações', 'Anotações')
    },
    {
      value: 'activities',
      label: 'Atividades',
      permission: usePermission('Visualizar lista de atividades', 'Arquivos e atividades complementares')
    },
    {
      value: 'projects',
      label: 'Projetos',
      permission: usePermission('Visualizar lista de projetos', 'Projetos')
    },
    {
      value: 'study_plans',
      label: 'Plano de estudos',
      permission: abilities?.['plano-de-estudo'] && isStudent
    }
  ].filter(option => option.permission);

  async function fetchSearchResults() {
    setIsLoading(true);

    const optionsNotSelected = filters.values.except.length > 0 ? typeOptions.filter(option => !filters.values.except.includes(option.value)) : [];
    const except = optionsNotSelected.map(option => option.value);
    const exceptString = except.map(item => `"${item}"`).join(',');

    const { updated, value } = filters.values;
    const { data } = await client.fetch(`/search?filter[updated]=${updated}&filter[value]=${value}&filter[except]=[${exceptString}]`);

    if (data) {
      setSearchData([...data]);
    }

    setIsLoading(false);
  }

  const handleOpenNotesModal = () => {
    setShowNotes(!showNotes);
    setNotesModalOpen(true);
  };

  function icon(type) {
    switch (type) {
      case 'LearningSystemItem':
        return getIcon('FiVideo');
      case 'LearningSystem':
        return getIcon('TbCopy');
      case 'Auditorium':
        return getIcon('LuMessagesSquare');
      case 'QuestionBook':
        return getIcon('FiTarget');
      case 'Post':
        return getIcon('FiCoffee');
      case 'LiveClassroom':
        return getIcon('CiStreamOn');
      case 'Note':
        return getIcon('FiClipboard');
      case 'Essay':
        return getIcon('FiEdit');
      case 'Activity':
        return getIcon('FiEdit3');
      default:
        return getIcon('FiVideo');
    }
  }

  async function handleNavigate(url, noteId = '') {
    if (url === '/') {
      setIsOpen(false);
      handleOpenNotesModal();
      const { data } = await client.fetch(['notes', noteId], {
        new_note: false
      });
      setActiveNote(data);
    } else {
      navigate(url);
    }

    setIsOpen(false);
  }

  useEffect(() => {
    if (isOpen && !isEmptyField) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        fetchSearchResults();
      }, 1000);
    }

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [filters.values.value]);

  useEffect(() => {
    if (filters.values.updated && !isEmptyField) {
      fetchSearchResults();
    }
  }, [filters.values.updated]);

  useEffect(() => {
    if (filters.values.except && isOpen) {
      fetchSearchResults();
    }
  }, [filters.values.except]);

  return (
    <>
      <NotificationsWrapper
        aria-label="Pesquisar"
        onClick={() => setIsOpen(true)}
      >
        <NotificationsToggle>
          <FiSearch />
        </NotificationsToggle>
      </NotificationsWrapper>

      <Dialog
        open={isOpen}
        onOpenChange={() => setIsOpen(false)}
      >
        <DialogContent
          size="md"
          hideCloseButton
        >
          <DialogTitle className="sr-only">Pesquisar</DialogTitle>

          <SearchWrapper>
            <SearchHeader>
              <Filter variant="plain">
                <FilterInput
                  startIcon={SearchIcon}
                  label={t('textsCommon.research')}
                  disabled={isLoading}
                  placeholder={'Pesquisar'}
                  type="search"
                  name="search"
                  value={filters.values.value}
                  onChange={e => filters.setFieldValue('value', e.target.value)}
                  wrapperClassName="grow"
                />

                <FilterSelectNative
                  disabled={isLoading}
                  label={'Última atualização'}
                  blankLabel={false}
                  value={filters.values.updated}
                  onChange={e => filters.setFieldValue('updated', e.target.value)}
                  options={lastUpdateOptions.map(o => ({
                    id: o.id,
                    name: o.title
                  }))}
                />
              </Filter>

              <FilterCombobox
                label="Filtrar por tipo"
                multiple
                fullWidth
                options={typeOptions}
                disabled={isEmptyField || isLoading}
                placeholder={isEmptyField ? 'Preencha o campo pesquisar...' : 'Selecione...'}
                emptyMessage="Sem opções"
                value={filters.values.except}
                onValueChange={val => filters.setFieldValue('except', val)}
              />
            </SearchHeader>

            <SearchBody>
              {!isLoading && searchData.length === 0 && (
                <EmptyState
                  type="data"
                  variant="plain"
                  title={filters.values.value === '' ? 'Nenhuma busca foi realizada' : 'Dados não encontrados'}
                  text={' '}
                />
              )}

              {isLoading && (
                <SearchLoaderWrapper>
                  <Loader />
                  <SearchLoaderTitle>Buscando os melhores resultados</SearchLoaderTitle>
                </SearchLoaderWrapper>
              )}

              {!isLoading &&
                searchData.length > 0 &&
                searchData.map(result => (
                  <SearchResult
                    key={result.id}
                    onClick={() => handleNavigate(result.path, result['entity-id'])}
                  >
                    <SearchResultIcon>{icon(result['entity-type'])}</SearchResultIcon>

                    <SearchResultContent>
                      <SearchResultHeader>
                        <SearchResultTitle>{result['translated-entity-type']}</SearchResultTitle>
                      </SearchResultHeader>

                      <SearchResultBody>
                        <SearchResultText>{result.content}</SearchResultText>
                      </SearchResultBody>
                    </SearchResultContent>

                    <SearchResultDate>{moment(result['updated-at']).format('DD/MM/YYYY')}</SearchResultDate>
                  </SearchResult>
                ))}
            </SearchBody>
          </SearchWrapper>
        </DialogContent>
      </Dialog>
    </>
  );
}
